import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Mail } from 'lucide-react';
import {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { Alert, Button, Input, LoadingState } from '@/components/ui';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const [forgotPassword, { isLoading, isSuccess, error }] = useForgotPasswordMutation();
  const [email, setEmail] = useState('');

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    void forgotPassword({ email });
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to choose a new one."
      footer={
        <Link to="/auth/login" className="font-medium text-brand-600 hover:underline">
          Back to sign in
        </Link>
      }
    >
      {isSuccess ? (
        // Deliberately the same message whether or not the address exists.
        <Alert tone="success" title="Check your inbox">
          If an account exists for that address, a reset link is on its way. The link
          expires in 30 minutes.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{apiErrorMessage(error)}</Alert>}
          <Input
            label="Email"
            type="email"
            icon={Mail}
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" size="lg" fullWidth loading={isLoading}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [resetPassword, { isLoading, isSuccess, error }] = useResetPasswordMutation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mismatch = confirm.length > 0 && password !== confirm;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (mismatch) return;
    void resetPassword({ token, password });
  };

  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => navigate('/auth/login', { replace: true }), 2000);
    return () => clearTimeout(timer);
  }, [isSuccess, navigate]);

  if (!token) {
    return (
      <AuthLayout title="Reset your password">
        <Alert tone="danger" title="This link is incomplete">
          Request a new reset link from the sign-in page.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="Signing in elsewhere will end those sessions.">
      {isSuccess ? (
        <Alert tone="success" title="Password changed">
          Taking you to the sign-in page…
        </Alert>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{apiErrorMessage(error)}</Alert>}
          <Input
            label="New password"
            type="password"
            icon={Lock}
            required
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint="At least 10 characters with upper case, lower case and a number."
          />
          <Input
            label="Confirm password"
            type="password"
            icon={Lock}
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            error={mismatch ? 'The passwords do not match' : undefined}
          />
          <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={mismatch}>
            Change password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [verifyEmail, { isLoading, isSuccess, isError, error }] = useVerifyEmailMutation();

  useEffect(() => {
    if (token) void verifyEmail({ token });
  }, [token, verifyEmail]);

  return (
    <AuthLayout
      title="Email verification"
      footer={
        <Link to="/auth/login" className="font-medium text-brand-600 hover:underline">
          Continue to sign in
        </Link>
      }
    >
      {!token && <Alert tone="danger">This verification link is incomplete.</Alert>}
      {isLoading && <LoadingState label="Verifying your email…" />}
      {isSuccess && <Alert tone="success" title="Verified">Your email address is confirmed.</Alert>}
      {isError && <Alert tone="danger">{apiErrorMessage(error)}</Alert>}
    </AuthLayout>
  );
}
